/**
 * Language context — bridges the user's saved `ui_language` preference and
 * react-i18next.
 *
 * On mount the active language comes from the browser/localStorage (handled by
 * i18next's LanguageDetector). Once the authenticated user's settings load, we
 * apply their saved `ui_language` so the choice follows them across devices.
 * Changing the language here updates i18next immediately and persists the
 * preference via the settings API.
 *
 * This is the *interface* language only — it does NOT touch response_language
 * (AI reply language) or any review-content language.
 */

import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { updateUserSettings } from '@/lib/api/settings'
import { useSettings } from '@/hooks/use-settings'
import { toSupportedLocale, type LocaleCode } from '@/i18n/locales'

interface LanguageContextValue {
  language: LocaleCode
  setLanguage: (code: LocaleCode) => Promise<void>
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation()
  const { settings } = useSettings()
  // Track whether we've already applied the server preference so a later manual
  // change isn't overwritten by a settings refetch.
  const appliedServerPref = useRef(false)

  useEffect(() => {
    if (settings?.ui_language && !appliedServerPref.current) {
      const code = toSupportedLocale(settings.ui_language)
      if (code !== i18n.resolvedLanguage) {
        void i18n.changeLanguage(code)
      }
      appliedServerPref.current = true
    }
  }, [settings?.ui_language, i18n])

  const setLanguage = useCallback(
    async (code: LocaleCode) => {
      appliedServerPref.current = true
      await i18n.changeLanguage(code)
      await updateUserSettings({ ui_language: code })
    },
    [i18n]
  )

  const value: LanguageContextValue = {
    language: toSupportedLocale(i18n.resolvedLanguage),
    setLanguage,
  }

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext)
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return ctx
}
